import { ehRiscoDeTanque, encaixarRiscoNoTanque, tanqueProximoDoPonto } from '../../../dominio/tabletop/encaixeRiscos'
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { PRODUTOS } from '../../../dominio/produtos'
import {
  adicionarObjeto,
  atualizarObjeto,
  criarCenaVazia,
  definirMeteorologia,
  encaixarPosicaoXZ,
  encaixarRotacaoGraus,
  type NovoObjetoTabletop,
  meteorologiaDaCena,
  removerObjeto,
  TIPOS_OBJETO_TABLETOP,
  type AtualizacoesObjetoTabletop,
  type CenaTabletop,
  type TipoObjetoTabletop,
  type TransformacaoTabletop,
} from '../../../dominio/tabletop'
import {
  camadasDaCena,
  classeDaMeteorologia,
  objetosEmRisco,
} from '../../../dominio/tabletop/camadas'
import {
  INJECOES_PADRAO,
  aplicarInjecao,
  type Injecao,
} from '../../../dominio/tabletop/injecoes'
import { PRESETS_MESA } from '../../../dominio/tabletop/presets'
import {
  desserializarSessaoMesa,
  sessaoDaCena,
  type SessaoMesa,
} from '../../../dominio/tabletop/sessao'
import { formatarDistancia } from '../../../dominio/unidades'
import { pontoCardeal } from '../../../dominio/vento'
import { CenaMesa3D } from '../../../visualizacao3d/tabletop/CenaMesa3D'
import {
  IconeCamadas,
  IconeModuloTabletop,
  IconeProcedimentos,
  IconeVento,
  IconeSelecao,
} from '../../icones'
import { useHistoricoDeModulo } from '../../useHistoricoDeModulo'
import { CascaOficina, type ContextoCasca } from '../../workspace/CascaOficina'
import { RosaDosVentos } from '../comum/RosaDosVentos'
import { ValorCitado } from '../comum/ValorCitado'
import { baixarArquivo, carregarMesa, CHAVE_MESA } from './persistencia'
import { volumeDaMesa } from './volume'
import { CatalogoObjetosMesa } from './CatalogoObjetosMesa'
import { direcaoGraus } from '../../../visualizacao3d/tabletop/manipulacaoMesa'

const nomeTipo = (tipo: TipoObjetoTabletop) =>
  adicionarObjeto(criarCenaVazia(), { tipo }, () => 'rotulo').objetos[0]!.nome

export function OficinaTabletop({ contexto }: { contexto: ContextoCasca }) {
  const [inicial] = useState(carregarMesa)
  const [sessao, setSessao] = useState(inicial.sessao)
  const [erro, setErro] = useState(inicial.erro)
  const [aviso, setAviso] = useState('')
  const [idSelecionado, setIdSelecionado] = useState<string | null>(null)
  const [tipoNovo, setTipoNovo] = useState<TipoObjetoTabletop>('viaturaHazmat')
  const [objetoParaPosicionar, setObjetoParaPosicionar] =
    useState<NovoObjetoTabletop | null>(null)
  const tipoParaPosicionar = objetoParaPosicionar?.tipo ?? null
  const [encaixePosicao, setEncaixePosicao] = useState(false)
  const [encaixeGiro, setEncaixeGiro] = useState(false)
  const previaObjeto = useMemo(
    () =>
      objetoParaPosicionar
        ? {
            ...objetoParaPosicionar,
            id: 'previa-posicionamento',
            nome:
              objetoParaPosicionar.nome ?? nomeTipo(objetoParaPosicionar.tipo),
            transformacao: objetoParaPosicionar.transformacao ?? {
              posicao: [0, 0, 0] as const,
              rotacao: [0, 0, 0] as const,
              escala: [1, 1, 1] as const,
            },
          }
        : null,
    [objetoParaPosicionar],
  )
  const [manipulando, setManipulando] = useState(false)
  const [ancoraCamera, setAncoraCamera] = useState(() =>
    volumeDaMesa(inicial.sessao.cena, []),
  )
  const [mostrarCamadas, setMostrarCamadas] = useState(true)
  const { cena } = sessao
  const historico = useHistoricoDeModulo(() => sessao, setSessao)
  const selecionado = cena.objetos.find((o) => o.id === idSelecionado) ?? null
  const m = meteorologiaDaCena(cena)
  const camadas = useMemo(() => camadasDaCena(cena), [cena])
  const volume = useMemo(
    () => volumeDaMesa(cena, mostrarCamadas ? camadas : [], ancoraCamera),
    [cena, camadas, mostrarCamadas, ancoraCamera],
  )
  const emRisco = useMemo(() => objetosEmRisco(cena, camadas), [cena, camadas])
  const idsEmRisco = useMemo(
    () => new Set(emRisco.map((r) => r.idObjeto)),
    [emRisco],
  )
  // mecânica leve: só sinaliza uma vez por objeto, sem travar a oficina nem poluir o roteiro a cada render
  const idsAlertados = useRef<Set<string>>(new Set())
  useEffect(() => {
    const novos = emRisco.filter((r) => !idsAlertados.current.has(r.idObjeto))
    if (novos.length === 0) return
    novos.forEach((r) => idsAlertados.current.add(r.idObjeto))
    setSessao((atual) => ({
      ...atual,
      registros: [
        ...atual.registros,
        ...novos.map((r) => {
          const objeto = cena.objetos.find((o) => o.id === r.idObjeto)
          return {
            titulo: 'Exposição a zona de risco',
            mensagem: `${objeto?.nome ?? 'Objeto'} entrou na zona de isolamento do GRE, a ${Math.round(r.distanciaM)} m da fonte.`,
          }
        }),
      ],
    }))
  }, [emRisco, cena.objetos])
  const { executar } = contexto.camera
  useEffect(
    () => executar('restaurar'),
    [volume.centro?.[0], volume.centro?.[1], volume.alturaAlvoM, executar],
  )
  useEffect(() => {
    try {
      window.localStorage.setItem(CHAVE_MESA, JSON.stringify(sessao))
    } catch {
      setErro(
        'Não foi possível salvar no navegador ou atualizar a projeção. Exporte o JSON para guardar a aula.',
      )
    }
  }, [sessao])

  function mudar(proxima: SessaoMesa, chave?: string) {
    historico.registrar(chave)
    setSessao(proxima)
  }
  function mudarCena(proxima: CenaTabletop, chave?: string) {
    mudar({ ...sessao, cena: proxima }, chave)
  }
  function editar(alteracoes: AtualizacoesObjetoTabletop, chave?: string) {
    if (selecionado && !selecionado.bloqueado)
      mudarCena(
        atualizarObjeto(cena, selecionado.id, {
          ...alteracoes,
          ...(alteracoes.fonte
            ? {
                fonte: {
                  idProduto: 'glp',
                  enchimento: 0.8,
                  ...selecionado.fonte,
                  ...alteracoes.fonte,
                },
              }
            : {}),
        }),
        chave,
      )
  }
  function trocarMesa(proxima: SessaoMesa) {
    idsAlertados.current.clear()
    mudar(proxima)
    setAviso('')
    setAncoraCamera(volumeDaMesa(proxima.cena, []))
    setObjetoParaPosicionar(null)
    setIdSelecionado(null)
  }
  function iniciarPosicionamento(tipo: TipoObjetoTabletop) {
    setTipoNovo(tipo)
    setObjetoParaPosicionar({
      tipo,
      ...(tipo === 'caminhaoTanque'
        ? { fonte: { idProduto: 'glp', enchimento: 0.8 } }
        : {}),
    })
    setIdSelecionado(null)
  }
  function posicionar(ponto: readonly [number, number]) {
    if (!objetoParaPosicionar || !previaObjeto) return
    const [x, z] = encaixarPosicaoXZ(...ponto, encaixePosicao ? 0.25 : null)
    let proxima = adicionarObjeto(cena, {
      ...objetoParaPosicionar,
      transformacao: {
        ...previaObjeto.transformacao,
        posicao: [x, previaObjeto.transformacao.posicao[1], z],
      },
    })
    const idTanque = ehRiscoDeTanque(objetoParaPosicionar.tipo) ? tanqueProximoDoPonto(cena, [x, z]) : null
    if (idTanque) proxima = encaixarRiscoNoTanque(proxima, proxima.objetos.at(-1)!.id, idTanque)
    mudarCena(proxima)
    setObjetoParaPosicionar(null)
    setIdSelecionado(proxima.objetos.at(-1)!.id)
  }
  function transformar(id: string, transformacao: TransformacaoTabletop) {
    const objeto = cena.objetos.find((o) => o.id === id)
    if (!objeto || objeto.bloqueado || objeto.oculto) return
    mudarCena(atualizarObjeto(cena, id, { transformacao, ...(objeto.idTanque && transformacao.posicao.some((p, i) => p !== objeto.transformacao.posicao[i]) ? { idTanque: null } : {}) }))
  }
  function girarSelecionado(graus: number) {
    if (!selecionado) return
    editar({
      transformacao: {
        rotacao: [
          selecionado.transformacao.rotacao[0],
          (encaixarRotacaoGraus(
            direcaoGraus(selecionado.transformacao.rotacao[1]) + graus,
            encaixeGiro ? 15 : null,
          ) *
            Math.PI) /
            180,
          selecionado.transformacao.rotacao[2],
        ],
      },
    })
  }
  function injetar(i: Injecao) {
    mudar({
      ...sessao,
      cena: aplicarInjecao(cena, i),
      registros: [
        ...sessao.registros,
        { titulo: i.titulo, mensagem: i.mensagem },
      ],
    })
    setAviso('')
  }
  async function importar(e: ChangeEvent<HTMLInputElement>) {
    const arquivo = e.target.files?.[0]
    e.target.value = ''
    if (!arquivo) return
    try {
      const proxima = desserializarSessaoMesa(await arquivo.text())
      trocarMesa(proxima)
      setErro('')
      setAviso('Aula importada.')
    } catch {
      setErro(
        'Arquivo inválido. A mesa atual foi mantida; escolha um JSON de cena ou de aula do QBRN Lab.',
      )
    }
  }
  function projetar() {
    try {
      window.localStorage.setItem(CHAVE_MESA, JSON.stringify(sessao))
      const url = new URL(window.location.href)
      url.hash = 'projecao-tabletop'
      const janela = window.open(url.href, 'qbrn-lab-projecao')
      if (!janela)
        setErro(
          'O navegador bloqueou a projeção. Permita a janela para exibir a mesa na segunda tela.',
        )
    } catch {
      setErro(
        'A projeção precisa de armazenamento local disponível neste navegador.',
      )
    }
  }
  const botao = (rotulo: string, acao: () => void, desabilitado = false) => (
    <button
      type="button"
      className="btn-acao-cad btn-acao-cad--secundario"
      onClick={acao}
      disabled={desabilitado}
    >
      {rotulo}
    </button>
  )

  const painelResultados = (
    <div className="grupo-controle">
      <h3 className="titulo-controle">Camadas calculadas</h3>
      <p className="nota-controle">
        Pluma gaussiana em terreno plano, sem obstáculos e com vento constante.
        Limites de Briggs: 100 m a 10 km. ZF fica além da ZM; o isolamento do
        GRE considera outros riscos.
      </p>
      {camadas.map((c) => (
        <section key={c.objeto.id} className="ficha-produto">
          <strong>
            {c.objeto.nome} — {c.produto.nome}
          </strong>
          <dl className="leituras">
            <div>
              <dt>GRE: isolar</dt>
              <dd>
                <ValorCitado
                  fonte={
                    c.acaoProtetora?.fonte ??
                    (c.objeto.fonte?.situacao === 'tanqueEmFogo'
                      ? c.produto.gre.isolamentoTanqueEmFogoM.fonte
                      : c.produto.gre.isolamentoInicialM.fonte)
                  }
                >
                  {formatarDistancia(c.isolamentoGreM)}
                </ValorCitado>
              </dd>
            </div>
            {c.bleve && (
              <>
                <div>
                  <dt>ZQ (5 kW/m²)</dt>
                  <dd>{formatarDistancia(c.bleve.zonas.raioQuenteM)}</dd>
                </div>
                <div>
                  <dt>ZM (1,6 kW/m²)</dt>
                  <dd>{formatarDistancia(c.bleve.zonas.raioMornaM)}</dd>
                </div>
              </>
            )}
            {c.pluma && (
              <div>
                <dt>Estabilidade</dt>
                <dd>
                  {c.pluma.classe} · {c.pluma.vazaoKgS} kg/s
                </dd>
              </div>
            )}
            {c.pluma?.contornos.map((p) => (
              <div key={p.limite.id}>
                <dt>
                  <ValorCitado fonte={p.limite.fonte}>
                    {p.limite.rotulo}
                  </ValorCitado>
                </dt>
                <dd>{formatarDistancia(p.alcanceM)}</dd>
              </div>
            ))}
            {c.acaoProtetora && (
              <div>
                <dt>GRE: proteger a favor do vento</dt>
                <dd>{formatarDistancia(c.acaoProtetora.protegerM)}</dd>
              </div>
            )}
          </dl>
          {c.avisos.map((a) => (
            <p className="aviso-modelo" key={a}>
              {a}
            </p>
          ))}
        </section>
      ))}
      {cena.objetos
        .filter(
          (o) =>
            o.fonte &&
            !o.oculto &&
            !PRODUTOS.some((p) => p.id === o.fonte!.idProduto),
        )
        .map((o) => (
          <p key={o.id} className="aviso-modelo">
            {o.nome}: produto desconhecido; nenhuma camada foi calculada.
            Selecione um produto do catálogo.
          </p>
        ))}
    </div>
  )

  const painelRoteiro = (
    <div className="grupo-controle">
      <h3 className="titulo-controle">Acionamento e roteiro</h3>
      <label className="campo">
        História inicial
        <textarea
          rows={4}
          value={cena.historiaInicial ?? ''}
          onChange={(e) =>
            mudarCena({ ...cena, historiaInicial: e.target.value }, 'historia')
          }
        />
      </label>
      {cena.acionamento && (
        <section className="ficha-produto">
          <strong>{cena.acionamento.natureza || 'Natureza a confirmar'}</strong>
          <p>
            {cena.acionamento.local || 'Local a confirmar'} ·{' '}
            {cena.acionamento.horario || 'Horário a confirmar'}
          </p>
          <p>{cena.acionamento.recursos || 'Recursos a confirmar'}</p>
          {cena.acionamento.informacoes
            .filter((i) => i.visivel)
            .map((i) => (
              <p key={i.id}>
                {i.confirmado ? 'Confirmado' : 'Relato'}: {i.texto}
              </p>
            ))}
        </section>
      )}
      <p className="nota-controle">
        Leia o evento à guarnição e discuta reposicionamento, isolamento, EPI e
        rotas. As fontes e o vento mudam na mesa; desfazer também remove o
        registro do evento.
      </p>
      {INJECOES_PADRAO.map((i) => (
        <button
          type="button"
          className="btn-acao-cad btn-acao-cad--secundario"
          key={i.id}
          onClick={() => injetar(i)}
        >
          {i.titulo}
        </button>
      ))}
      <h3 className="titulo-controle">Debriefing</h3>
      <ol className="mesa-registros">
        {sessao.registros.map((r, i) => (
          <li key={i}>
            <strong>{r.titulo}</strong>
            <p>{r.mensagem}</p>
          </li>
        ))}
      </ol>
      <label className="campo">
        Decisões e aprendizados
        <textarea
          rows={5}
          placeholder="Como a equipe reagiu? O que mudaria no próximo exercício?"
          value={sessao.debriefing}
          onChange={(e) =>
            mudar({ ...sessao, debriefing: e.target.value }, 'debriefing')
          }
        />
      </label>
      {botao('Exportar debriefing', () =>
        baixarArquivo(
          `# ${cena.nome}\n\n${cena.historiaInicial ?? ''}\n\n${sessao.registros.map((r, i) => `${i + 1}. ${r.titulo}\n   ${r.mensagem}`).join('\n\n')}\n\n## Decisões e aprendizados\n\n${sessao.debriefing}\n`,
          'debriefing-qbrn.md',
          'text/markdown',
        ),
      )}
    </div>
  )

  const painelSelecao = selecionado ? (
    <div className="grupo-controle">
      <label className="campo">
        Nome do objeto
        <input
          value={selecionado.nome}
          disabled={selecionado.bloqueado}
          onChange={(e) => {
            if (e.target.value.trim())
              editar({ nome: e.target.value }, `nome-${selecionado.id}`)
          }}
        />
      </label>
      {ehRiscoDeTanque(selecionado.tipo) && (
        <fieldset disabled={selecionado.bloqueado}>
          <legend>Encaixe no tanque</legend>
          <label className="campo">
            Tanque vinculado
            <select value={selecionado.idTanque ?? ''} onChange={e => mudarCena(encaixarRiscoNoTanque(cena, selecionado.id, e.target.value || null))}>
              <option value="">Livre na cena</option>
              {cena.objetos.filter(o => o.tipo === 'caminhaoTanque').map(o => <option key={o.id} value={o.id}>{o.nome}</option>)}
            </select>
          </label>
          <p>O efeito acompanha o tanque. Arrastar a origem ou editar a posição deixa o efeito livre.</p>
          {selecionado.tipo === 'bleve' && <p>Bola de fogo ilustrativa; os raios calculados continuam nas camadas BLEVE.</p>}
        </fieldset>
      )}
      {([0, 1, 2] as const).map((indice) => (
        <label className="campo" key={indice}>
          Posição {indice === 0 ? 'X' : indice === 1 ? 'Y' : 'Z'} (m)
          <input
            type="number"
            step={1}
            value={selecionado.transformacao.posicao[indice]}
            disabled={selecionado.bloqueado}
            onChange={(e) => {
              if (
                e.target.value === '' ||
                !Number.isFinite(e.target.valueAsNumber)
              )
                return
              const posicao: [number, number, number] = [
                ...selecionado.transformacao.posicao,
              ]
              posicao[indice] = e.target.valueAsNumber
              editar(
                { transformacao: { posicao }, ...(selecionado.idTanque ? { idTanque: null } : {}) },
                `posicao-${selecionado.id}-${indice}`,
              )
            }}
          />
        </label>
      ))}
      <label className="deslizante">
        <span>Rotação no chão</span>
        <input
          type="range"
          min={0}
          max={360}
          step={1}
          value={direcaoGraus(selecionado.transformacao.rotacao[1])}
          disabled={selecionado.bloqueado}
          onChange={(e) =>
            editar(
              {
                transformacao: {
                  rotacao: [
                    selecionado.transformacao.rotacao[0],
                    (encaixarRotacaoGraus(
                      Number(e.target.value),
                      encaixeGiro ? 15 : null,
                    ) *
                      Math.PI) /
                      180,
                    selecionado.transformacao.rotacao[2],
                  ],
                },
              },
              `rotacao-${selecionado.id}`,
            )
          }
        />
      </label>
      <label className="toggle">
        <input
          type="checkbox"
          checked={!!selecionado.bloqueado}
          onChange={(e) =>
            mudarCena(
              atualizarObjeto(cena, selecionado.id, {
                bloqueado: e.target.checked,
              }),
            )
          }
        />
        Bloquear edição do objeto
      </label>
      <label className="toggle">
        <input
          type="checkbox"
          checked={!!selecionado.oculto}
          onChange={(e) =>
            mudarCena(
              atualizarObjeto(cena, selecionado.id, {
                oculto: e.target.checked,
              }),
            )
          }
        />
        Ocultar objeto e suas camadas
      </label>
      {(selecionado.fonte || selecionado.tipo === 'caminhaoTanque') && (
        <fieldset disabled={selecionado.bloqueado} className="mesa-fonte">
          <legend>Fonte de produto</legend>
          <label className="campo">
            Produto
            <select
              value={selecionado.fonte?.idProduto ?? 'glp'}
              onChange={(e) => editar({ fonte: { idProduto: e.target.value } })}
            >
              {selecionado.fonte &&
                !PRODUTOS.some(
                  (p) => p.id === selecionado.fonte!.idProduto,
                ) && (
                  <option value={selecionado.fonte.idProduto}>
                    Produto desconhecido
                  </option>
                )}
              {PRODUTOS.map((p) => (
                <option value={p.id} key={p.id}>
                  {p.nome} · ONU {p.onu}
                </option>
              ))}
            </select>
          </label>
          <label className="campo">
            Situação da fonte
            <select
              value={selecionado.fonte?.situacao ?? 'estavel'}
              onChange={(e) =>
                editar({
                  fonte: {
                    situacao: e.target.value as
                      'estavel' | 'vazamento' | 'tanqueEmFogo',
                  },
                })
              }
            >
              <option value="estavel">Estável</option>
              <option value="vazamento">Vazamento</option>
              <option value="tanqueEmFogo">Tanque em fogo</option>
            </select>
          </label>
          <label className="deslizante">
            <span>
              Enchimento{' '}
              {Math.round((selecionado.fonte?.enchimento ?? 0.8) * 100)}%
            </span>
            <input
              type="range"
              min={0}
              max={100}
              value={(selecionado.fonte?.enchimento ?? 0.8) * 100}
              onChange={(e) =>
                editar(
                  { fonte: { enchimento: Number(e.target.value) / 100 } },
                  `enchimento-${selecionado.id}`,
                )
              }
            />
          </label>
          <label className="campo">
            Vazão (kg/s)
            <input
              type="number"
              min={0}
              step={0.1}
              value={selecionado.fonte?.vazaoKgS ?? 2}
              onChange={(e) => {
                if (
                  e.target.value !== '' &&
                  Number.isFinite(e.target.valueAsNumber) &&
                  e.target.valueAsNumber >= 0
                )
                  editar(
                    { fonte: { vazaoKgS: e.target.valueAsNumber } },
                    `vazao-${selecionado.id}`,
                  )
              }}
            />
          </label>
        </fieldset>
      )}
      <div className="mesa-acoes">
        {botao('Duplicar objeto', () => {
          setObjetoParaPosicionar({
            tipo: selecionado.tipo,
            nome: `${selecionado.nome} (cópia)`,
            transformacao: selecionado.transformacao,
            ...(selecionado.cor ? { cor: selecionado.cor } : {}),
            ...(selecionado.fonte ? { fonte: { ...selecionado.fonte } } : {}),
          })
          setIdSelecionado(null)
        })}
        {botao(
          'Remover objeto',
          () => {
            mudarCena(removerObjeto(cena, selecionado.id))
            setIdSelecionado(null)
          },
          !!selecionado.bloqueado,
        )}
      </div>
    </div>
  ) : (
    <p className="nota-controle">
      Selecione um objeto na cena ou na lista para editar.
    </p>
  )

  const grupos = [
    {
      id: 'mesa',
      rotulo: 'Mesa',
      icone: <IconeModuloTabletop />,
      conteudo: () => (
        <div className="grupo-controle">
          <label className="campo">
            Nome da mesa
            <input
              value={cena.nome}
              onChange={(e) =>
                mudarCena({ ...cena, nome: e.target.value }, 'nome-mesa')
              }
            />
          </label>
          <span className="titulo-controle">Cenas de aula</span>
          {PRESETS_MESA.map((p) => (
            <button
              type="button"
              className="btn-acao-cad btn-acao-cad--secundario"
              key={p.id}
              title={p.resumo}
              onClick={() => {
                trocarMesa(sessaoDaCena(p.cena))
                setAviso(p.resumo)
              }}
            >
              {p.titulo}
            </button>
          ))}
          {botao('Nova mesa vazia', () => {
            trocarMesa(sessaoDaCena(criarCenaVazia()))
          })}
          {botao('Exportar aula JSON', () =>
            baixarArquivo(JSON.stringify(sessao, null, 2), 'mesa-qbrn.json'),
          )}
          <label className="campo">
            Importar aula JSON
            <input
              type="file"
              accept=".json,application/json"
              onChange={importar}
            />
          </label>
          {botao('Projetar em segunda tela', projetar)}
          <p className="nota-controle">
            A aula é salva neste navegador. Exporte o JSON para compartilhar a
            cena, os eventos e o debriefing. A projeção acompanha as alterações
            nesta origem.
          </p>
        </div>
      ),
    },
    {
      id: 'objetos',
      rotulo: 'Objetos',
      icone: <IconeSelecao />,
      resumo: `${cena.objetos.length} objetos`,
      conteudo: () => (
        <div className="grupo-controle">
          <label className="campo">
            Adicionar à mesa
            <select
              value={tipoNovo}
              onChange={(e) =>
                setTipoNovo(e.target.value as TipoObjetoTabletop)
              }
            >
              {TIPOS_OBJETO_TABLETOP.map((t) => (
                <option value={t} key={t}>
                  {nomeTipo(t)}
                </option>
              ))}
            </select>
          </label>
          {botao('Posicionar objeto', () => iniciarPosicionamento(tipoNovo))}
          <p className="nota-controle">
            Escolha o item, pressione Posicionar objeto e clique no ponto da
            mesa. Depois arraste o objeto para mover e a alça laranja para
            girar.
          </p>
          <label className="toggle">
            <input
              type="checkbox"
              checked={encaixePosicao}
              onChange={(e) => setEncaixePosicao(e.target.checked)}
            />
            Encaixar posição em 0,25 m
          </label>
          <label className="toggle">
            <input
              type="checkbox"
              checked={encaixeGiro}
              onChange={(e) => setEncaixeGiro(e.target.checked)}
            />
            Encaixar giro em 15°
          </label>
          <CatalogoObjetosMesa
            nomeTipo={nomeTipo}
            aoEscolher={iniciarPosicionamento}
          />
          <div className="mesa-lista" role="group" aria-label="Objetos da mesa">
            {cena.objetos.map((o) => (
              <button
                type="button"
                className="btn-acao-cad btn-acao-cad--secundario"
                key={o.id}
                aria-pressed={selecionado?.id === o.id}
                onClick={() => setIdSelecionado(o.id)}
              >
                {o.nome}
                {o.oculto ? ' (oculto)' : ''}
              </button>
            ))}
          </div>
        </div>
      ),
    },
    {
      id: 'selecao',
      rotulo: 'Editar seleção',
      icone: <IconeSelecao />,
      conteudo: () => painelSelecao,
    },
    {
      id: 'meteorologia',
      rotulo: 'Meteorologia',
      icone: <IconeVento />,
      resumo: `${pontoCardeal(m.direcaoVentoGraus)} · ${m.ventoMS} m/s`,
      conteudo: () => (
        <div className="grupo-controle">
          <RosaDosVentos
            direcaoGraus={m.direcaoVentoGraus}
            aoMudar={(v) =>
              mudarCena(
                definirMeteorologia(cena, { direcaoVentoGraus: v }),
                'vento-direcao',
              )
            }
          />
          <label className="deslizante">
            <span>Velocidade do vento {m.ventoMS} m/s</span>
            <input
              type="range"
              min={1}
              max={10}
              step={0.5}
              value={m.ventoMS}
              onChange={(e) =>
                mudarCena(
                  definirMeteorologia(cena, {
                    ventoMS: Number(e.target.value),
                  }),
                  'vento-velocidade',
                )
              }
            />
          </label>
          <label className="campo">
            Período
            <select
              value={m.periodo}
              onChange={(e) =>
                mudarCena(
                  definirMeteorologia(cena, {
                    periodo: e.target.value as 'dia' | 'noite',
                  }),
                )
              }
            >
              <option value="dia">Dia</option>
              <option value="noite">Noite</option>
            </select>
          </label>
          <label className="toggle">
            <input
              type="checkbox"
              checked={m.ceuEncoberto}
              onChange={(e) =>
                mudarCena(
                  definirMeteorologia(cena, { ceuEncoberto: e.target.checked }),
                )
              }
            />
            Céu encoberto
          </label>
          <label className="campo">
            Terreno
            <select
              value={m.terreno}
              onChange={(e) =>
                mudarCena(
                  definirMeteorologia(cena, {
                    terreno: e.target.value as 'aberto' | 'urbano',
                  }),
                )
              }
            >
              <option value="aberto">Campo aberto</option>
              <option value="urbano">Área urbana</option>
            </select>
          </label>
        </div>
      ),
    },
    {
      id: 'camadas',
      rotulo: 'Camadas',
      icone: <IconeCamadas />,
      conteudo: () => (
        <div className="grupo-controle">
          <label className="toggle">
            <input
              type="checkbox"
              checked={mostrarCamadas}
              onChange={(e) => setMostrarCamadas(e.target.checked)}
            />
            Mostrar camadas calculadas
          </label>
          {painelResultados}
        </div>
      ),
    },
    {
      id: 'roteiro',
      rotulo: 'Roteiro e debriefing',
      icone: <IconeProcedimentos />,
      conteudo: () => painelRoteiro,
    },
  ]
  return (
    <CascaOficina
      contexto={contexto}
      oficina={{
        volume,
        distanciaMaxima: 24000,
        orbitaHabilitada: !manipulando,
        recolherFerramentas: tipoParaPosicionar !== null,
        comEnquadrarCena: true,
        idSelecionado: selecionado?.id,
        nomeSelecionado: selecionado?.nome,
        aoClicarVazio: () => setIdSelecionado(null),
        historico,
        ferramentas: {
          grupos,
          chips: [
            {
              id: 'objetos',
              rotulo: 'Objetos',
              valor: String(cena.objetos.length),
            },
            {
              id: 'vento',
              rotulo: 'Vento',
              valor: `${pontoCardeal(m.direcaoVentoGraus)} · ${m.ventoMS} m/s`,
            },
            { id: 'classe', rotulo: 'Classe', valor: classeDaMeteorologia(m) },
            {
              id: 'eventos',
              rotulo: 'Eventos',
              valor: String(sessao.registros.length),
            },
          ],
          inspetor: selecionado
            ? {
                titulo: selecionado.nome,
                subtitulo: nomeTipo(selecionado.tipo),
                acoes: painelSelecao,
                aoFechar: () => setIdSelecionado(null),
              }
            : null,
          acoesRapidas: (
            <div className="mesa-acoes">
              {botao(
                'Desfazer alteração',
                historico.desfazer,
                !historico.podeDesfazer,
              )}
              {botao(
                'Refazer alteração',
                historico.refazer,
                !historico.podeRefazer,
              )}
            </div>
          ),
        },
        operacao: painelRoteiro,
        checklist: painelResultados,
        sobreposicao: (
          <div className="mesa-mensagens">
            {tipoParaPosicionar && (
              <div className="mesa-manipulacao" role="status">
                <span>{previaObjeto?.nome}: clique no ponto da mesa.</span>
                {botao('Cancelar posicionamento', () =>
                  setObjetoParaPosicionar(null),
                )}
              </div>
            )}
            {!tipoParaPosicionar && selecionado && (
              <div
                className="mesa-manipulacao"
                aria-label="Mover e girar objeto"
              >
                <strong>{selecionado.nome}</strong>
                <span>
                  {selecionado.bloqueado
                    ? 'Objeto bloqueado'
                    : 'Arraste para mover; no toque, segure por um instante. Use dois dedos para zoom.'}
                </span>
                <div className="mesa-acoes">
                  {botao(
                    'Girar −15°',
                    () => girarSelecionado(-15),
                    selecionado.bloqueado,
                  )}
                  {botao(
                    'Girar +15°',
                    () => girarSelecionado(15),
                    selecionado.bloqueado,
                  )}
                </div>
              </div>
            )}

            {erro && (
              <p className="aviso-modelo" role="alert">
                {erro}
              </p>
            )}
            {(aviso || sessao.registros.at(-1)?.mensagem) && (
              <p className="aviso-modelo" role="status">
                {aviso || sessao.registros.at(-1)?.mensagem}
              </p>
            )}
          </div>
        ),
        cena: (
          <CenaMesa3D
            cena={cena}
            camadas={camadas}
            mostrarCamadas={mostrarCamadas}
            selecionado={selecionado?.id}
            aoSelecionar={setIdSelecionado}
            tipoParaPosicionar={tipoParaPosicionar}
            previaObjeto={previaObjeto}
            passoPosicaoM={encaixePosicao ? 0.25 : null}
            passoGiroGraus={encaixeGiro ? 15 : null}
            aoPosicionar={posicionar}
            aoTransformar={transformar}
            aoManipular={setManipulando}
            aoCancelarPosicionamento={() => setObjetoParaPosicionar(null)}
            idsEmRisco={idsEmRisco}
          />
        ),
      }}
    />
  )
}
